import React, { useMemo } from 'react';
import { useWebSocket } from '../hooks/whatsapp/useWebSocket';
import { useChatNotifications } from '../hooks/whatsapp/useChatNotifications';
import { useWaPermissions } from '../hooks/whatsapp/useWaPermissions';

/**
 * Conecta o WebSocket do chat WhatsApp e registra os listeners de notificacao.
 *
 * Fica num componente proprio porque hooks nao podem ser chamados
 * condicionalmente — o gating de permissao decide se ESTE componente e
 * montado (no provider abaixo), e so entao os hooks rodam.
 *
 * Antes, useWebSocket()/useChatNotifications() viviam em ChatPage (so na tela
 * de atendimento). Por isso o sino global so recebia mensagens enquanto o
 * agente estava com o chat aberto. Aqui eles rodam no nivel da aplicacao, para
 * que new_message chegue ao sino em qualquer modulo do sistema.
 */
function WhatsAppNotificationConnector() {
  useWebSocket();
  useChatNotifications();
  return null;
}

/**
 * Provider global do WebSocket de notificacoes do WhatsApp.
 *
 * Monta a conexao WS do chat no App, mas SOMENTE para usuarios que podem
 * atender/ver o chat. Quem nao tem nenhuma permissao de chat (ex: alguem que
 * so usa o modulo Fiscal) nao abre conexao WS ociosa.
 *
 * O gating reusa useWaPermissions, que ja trata admin e o modo bootstrap
 * (telas do WhatsApp ainda nao cadastradas no backend -> libera).
 */
export function WhatsAppNotificationProvider({ children }: { children: React.ReactNode }) {
  const perms = useWaPermissions();

  // "Pode usar o chat" = qualquer permissao de atendimento de chat.
  // Cobre quem responde, assume, finaliza, faz nota interna ou abre chamado.
  const canUseChat = useMemo(
    () =>
      perms.canResponder ||
      perms.canAssumir ||
      perms.canFinalizar ||
      perms.canNotaInterna ||
      perms.canAbrirChamado,
    [perms],
  );

  return (
    <>
      {canUseChat && <WhatsAppNotificationConnector />}
      {children}
    </>
  );
}
